import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, catchError, delayWhen, map, Observable, of, Subject, switchMap, take, takeUntil } from 'rxjs';
import { Application } from 'src/portaljs/constants/application';
import { IframeMetadata } from 'src/portaljs/models/iframeMetadata';
import { PortalApp } from '../shared/portal-app';
import { AuthService } from './auth.service';
import { GrantsModel, GrantsPermissions } from '../models/grants.model';
import { EnvironmentService } from './environment.service';
import { AppInfo } from '../models/app-info.model';
import * as _ from 'lodash-es';
import AuthType from '../enums/auth.type.enum';
import { ChannelService } from './channel.service';
import { PortalMessagingService } from './portal-messaging.service';
import { ConsoleLogger } from '../shared/console-logger';
import { DialogModeType } from '../shared/error/dialog-message-type.enum';
import { UserMessage } from '../shared/error/user-message';
import { ToastMessageService } from './toast-message.service';
import { UnifiedSettingsService } from './unified-settings.service';

@Injectable({
  providedIn: 'root'
})
export class IframeService implements OnDestroy {
  private readonly ngUnsubscribe = new Subject();
  private readonly _cancelWaitForApps = new Subject<void>();
  private readonly newlyAddedEmbeddedFrameApps: Application[] = [];
  private initialNavInfoForIframeFromBookmarkUrl: {app: string, nav: string} = null;
  public DESTINY_RESET_IN_PROGRESS_HACK_INDICATOR = false;
  public continuationState: Record<string, { continuationState: any }> = {};

  // DO NOT change these array references. Changing the underlying array references will cause all iframes to be re-created and user state will be lost
  // DO NOT change these array references. Changing the underlying array references will cause all iframes to be re-created and user state will be lost
  private readonly embeddedFrames: PortalApp[] = [];
  private _embeddedFrames: BehaviorSubject<PortalApp[]> = new BehaviorSubject<PortalApp[]>(this.embeddedFrames);
  private embeddedFrames$: Observable<PortalApp[]> = this._embeddedFrames.asObservable();

  private readonly overlayFrames: PortalApp[] = [];
  private _overlayFrames: BehaviorSubject<PortalApp[]> = new BehaviorSubject<PortalApp[]>(this.overlayFrames);
  private overlayFrames$: Observable<PortalApp[]> = this._overlayFrames.asObservable();
  // DO NOT change these array references. Changing the underlying array references will cause all iframes to be re-created and user state will be lost
  // DO NOT change these array references. Changing the underlying array references will cause all iframes to be re-created and user state will be lost

  private _metadataMessage: BehaviorSubject<Record<Application, IframeMetadata>> = new BehaviorSubject<Record<Application, IframeMetadata>>(undefined);
  private metadataMessage$: Observable<Record<Application, IframeMetadata>> = this._metadataMessage.asObservable();


  /** ----------------------------------------------------------------------------------------------------------------------- */
  constructor(private envService: EnvironmentService, private authService: AuthService,
              private channelService: ChannelService, private messagingService: PortalMessagingService,
              private toastService: ToastMessageService, private unsService: UnifiedSettingsService) {
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this._cancelWaitForApps.next();
    this._cancelWaitForApps.complete();
  }


  /** ----------------------------------------------------------------------------------------------------------------------- */
  /**
   * Determines if the Juniper Sidecar (AI chatbot) should be enabled for the current user.
   *
   * Requirements (ALL must be met):
   * 1. User must be logged in and at a site (not DestinyAdminNotAtSite)
   * 2. Sidecar must be enabled based on environment and UNS settings:
   *    - If UNS explicitly enables (true): SHOW regardless of environment
   *    - If UNS explicitly disables (false): DO NOT SHOW regardless of environment
   *    - If UNS not set (undefined): Follow environment (enabled in non-prd, disabled in prd)
   * 3. Destiny chatbot must be enabled (grants.destinyChatbotEnabled === true)
   * 4. User must have at least one of the required permissions:
   *    - JUNIPER_SIDECAR_ACCESS_LIBRARY
   *    - JUNIPER_SIDECAR_ACCESS_RESOURCE
   *
   * @param grants - The user's grants containing permissions and destinyChatbotEnabled flag
   * @returns true if all requirements are met, false otherwise
   */
  public isJuniperSidecarEnabled(grants: GrantsModel) {
    let isEnabled = false;
    if (grants) {
      const ulm = this.authService.getUserLogin();
      // LCP-604: Check destiny.ChatbotEnabled site attribute from AASP (added to grants by portal BE)
      // LCP-620: Also check if customer number is whitelisted for Sidecar in districts settings
      // LCP-708: Check destiny.ChatbotEnabled district attribute from AASP (serviceEndpointInfo) for non-destinyAdmin district users at the district
      ConsoleLogger.debug(ulm? ('isJuniperSidecarEnabled :: User is at site: ' + !ulm.isDestinyAdminNotAtSite() + '; ') : 'ulm undefined');
      ConsoleLogger.debug('isJuniperSidecarEnabled :: unsService.isFollettAIEnabled() - ' + this.unsService.getSideCarScope() + '; grants.destinyChatbotEnabled - ' + grants.destinyChatbotEnabled + ';Chatbot enabled at district - ' + this.envService.destinyChatBotEnabledAtDistrict);

      const enabledInUns: boolean | undefined = this.unsService.getSideCarScope();
      const enabledForEnvironment = true; // !this.envService.isPrd();
      const userAtSite = !!ulm && !ulm.isDestinyAdminNotAtSite();
      const districtUserAtDistrict = !!ulm && ulm.isDistrictUserNotAtSite();

      let showSidecar = false;
      if (enabledInUns === true) {
        showSidecar = true; // Explicitly enabled by UNS, SHOW even if not enabled for environment.
      } else if (enabledInUns === false) {
        showSidecar = false; // Explicitly disabled by UNS, DO NOT SHOW, even if enabled for environment -- DO NOT SHOW.
      } else {
        showSidecar = enabledForEnvironment; // If not explicitly set by UNS, follow the environment setting.
      }

      if(showSidecar) {
        let chatbotEnabled = false;
        if(districtUserAtDistrict && this.envService.destinyChatBotEnabledAtDistrict){
          chatbotEnabled = true;
        } else if(userAtSite){
          chatbotEnabled = grants.destinyChatbotEnabled === true;
        }

        // Permissions check: the user must have the appropriate permissions to see the Sidecar
        if (chatbotEnabled){
          const perms = grants.permissions || [];
          const jsaLibrary: boolean = perms.indexOf(GrantsPermissions.JUNIPER_SIDECAR_ACCESS_LIBRARY) >= 0;
          const jsaResource: boolean = perms.indexOf(GrantsPermissions.JUNIPER_SIDECAR_ACCESS_RESOURCE) >= 0;
          isEnabled = jsaLibrary || jsaResource;
        }
      }
    }
    return isEnabled;
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public getMetadataMessage$(): Observable<Record<Application, IframeMetadata>> {
    return this.metadataMessage$;
  }
  public updateIframeMetadata(app: Application, iframeMetadata: IframeMetadata): void {
    if (app) {
      const current = this._metadataMessage.value ?? {} as Record<Application, IframeMetadata>;
      const updated = { ...current, [app]: iframeMetadata };
      this._metadataMessage.next(updated);
    }
  }


  /** ----------------------------------------------------------------------------------------------------------------------- */
  public getOverlayFrames(): PortalApp[] {
    return this._overlayFrames.value;
  }
  public getOverlayFrames$(): Observable<PortalApp[]> {
    return this.overlayFrames$;
  }
  public findOverlayFrame(app: Application): PortalApp {
    return this.getOverlayFrames().find(f => f.app === app);
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public getEmbeddedFrames$(): Observable<PortalApp[]> {
    return this.embeddedFrames$;
  }
  public getEmbeddedFrames(): PortalApp[] {
    return this._embeddedFrames.value;
  }
  public addEmbeddedFrame(app: PortalApp) {
    this.embeddedFrames.push(app);
    this._embeddedFrames.next(this.embeddedFrames);
  }
  public findEmbeddedFrame(app: Application): PortalApp {
    return this.getEmbeddedFrames().find(f => f.app === app);
  }


  /** ----------------------------------------------------------------------------------------------------------------------- */
  public findEmbeddedOrOverlayFrame(app: Application): PortalApp {
    return this.findEmbeddedFrame(app) || this.findOverlayFrame(app);
  }
  public getEmbeddedAndOverlayFrames(): PortalApp[] {
    return [...this.getEmbeddedFrames(), ...this.getOverlayFrames()];
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public removeFromNewlyAddedEmbeddedFrameApps(app: Application) {
    _.remove(this.newlyAddedEmbeddedFrameApps, (a) => a === app);
  }
  public isInNewlyAddedEmbeddedFrameApps(app: Application): boolean {
    return this.newlyAddedEmbeddedFrameApps.includes(app);
  }
  public addToNewlyAddedEmbeddedFrameApps(app: Application) {
    if (!this.isInNewlyAddedEmbeddedFrameApps(app)) {
      this.newlyAddedEmbeddedFrameApps.push(app);
    }
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public setReadyResponseReceived(app: Application, responseReceived: boolean) {
    if (app) {
      const fa = this.findEmbeddedOrOverlayFrame(app);
      if (fa) {
        fa.setReadyResponseReceived(responseReceived);
      }
    }
  }
  public isReadyResponseReceived(app: PortalApp): boolean {
    if (app) {
      return (app.app === Application.PORTAL || (!!app.isReadyResponseReceived()));
    }
    return false;
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public markAppsWithLoadingError(apps: PortalApp[]): PortalApp[] {
    if (!this.envService.isProductionBuild() && localStorage.getItem('preserveIframes') === 'true') {
      return [];
    }

    const unreadyApps = [];
    for (const app of apps) {
      if (!(app.app === Application.PORTAL || app.isReadyResponseReceived())) {
        app.loadingError = true;
        app.url = 'assets/unready-app-error.html';
        app.setReadyResponseReceived(true);
        unreadyApps.push(app);
      }
    }

    return unreadyApps;
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public hideAllEmbeddedFrames() {
    for (const app of this.getEmbeddedFrames()) {
      app.accessible = false;
    }
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public setInitialNavInfoForIframeFromBookmarkUrl(navInfo: {app: string, nav: string}) {
    this.initialNavInfoForIframeFromBookmarkUrl = navInfo;
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public addToEmbeddedFrames(newApp: PortalApp, siteGuidOverride?: string): Observable<PortalApp[]> {
    if (!newApp || newApp.isExternal || newApp.app === Application.PORTAL || this.getEmbeddedFrames().some(f => f.app === newApp.app)) {
      this.removeFromNewlyAddedEmbeddedFrameApps(newApp.app);
      return of([]);
    } else {
      const appInfo: AppInfo = this.envService.appInfos.find((ai: any) => ai.name === newApp.app);
      const appId = appInfo?.appId;
      if (!appId) {
        return of([]);
      }

      this.addToNewlyAddedEmbeddedFrameApps(newApp.app);

      return this.authService.getAppAuthCodes(appId, 'addEmbedded')
        .pipe(
          switchMap((authCodes: any) => {
            const authCode: string = authCodes[appId];
            newApp.url = this.envService.getUrl(newApp.app);
            if(appId && authCode) {
              newApp.url = this.fixEmbeddedUrl(newApp.url, newApp.app, authCode, appInfo.override, siteGuidOverride);
              if (!this.findEmbeddedFrame(newApp.app)) {
                ConsoleLogger.debug('IframeService.addToEmbeddedFrames() - creating new frame for: ' + JSON.stringify(newApp.app));
                this.addEmbeddedFrame(newApp);
              }
            }

            // Give the app time to respond with a 'ready' message.
            return this.waitForApps([newApp], [newApp], this.getEmbeddedFrames());
          }),
          take(1),
          takeUntil(this.ngUnsubscribe)
        );
    }
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  private getOrCreateSidecarApp(grants: GrantsModel): PortalApp {
    let sidecarApp = this.overlayFrames.find(a => a.app === Application.JUNIPER_SIDECAR);
    if (!sidecarApp) { // There can be only one
      if (this.isJuniperSidecarEnabled(grants)) {
        ConsoleLogger.debug('IframeService.getOrCreateSidecarApp() - Adding Juniper Sidecar frame');
        const url = this.envService.getUrl(Application.JUNIPER_SIDECAR);
        if (url) {
          sidecarApp = new PortalApp(Application.JUNIPER_SIDECAR, url, false, null);
          sidecarApp.isOverlay = true;
          this.overlayFrames.push(sidecarApp);
          this._overlayFrames.next(this.overlayFrames);
          ConsoleLogger.debug('IframeService.getOrCreateSidecarApp() - Juniper Sidecar frame added: ', sidecarApp);
        } else {
          ConsoleLogger.debug('IframeService.getOrCreateSidecarApp() - Juniper Sidecar URL not found in environment configuration.');
        }
      }
    }
    return sidecarApp;
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public processOverlayFrames(grants: GrantsModel): Observable<any> {
    ConsoleLogger.debug('IframeService.processOverlayFrames() - start');

    // Cancel ALL previous waitForApps observables for overlay apps
    ConsoleLogger.debug('IframeService.processOverlayFrames() - Cancelling previous waitForApps calls');
    this._cancelWaitForApps.next();

    const appInfo: AppInfo = this.envService.appInfos.find(a => a.name === Application.JUNIPER_SIDECAR);
    if (!appInfo) {
      ConsoleLogger.debug('IframeService.processOverlayFrames() - Juniper Sidecar AppInfo not found');
      return of(undefined);
    }

    const sidecarApp = this.getOrCreateSidecarApp(grants);
    ConsoleLogger.debug('IframeService.processOverlayFrames() - sidecarApp: ', sidecarApp);
    const authType = this.authService.getAuthenticationEvent()?.authType;
    ConsoleLogger.debug('IframeService.processOverlayFrames() - authType: ', authType);
    if (this.isJuniperSidecarEnabled(grants)) {
      ConsoleLogger.debug('IframeService.processOverlayFrames() - Juniper Sidecar access granted');
      if (!sidecarApp) {
        ConsoleLogger.error('Juniper Sidecar enabled but app creation failed');
        return of(undefined);
      }

      return this.authService.getAppAuthCodes(appInfo.appId, 'iframeoverlay')
        .pipe(
          catchError((err) => {
            ConsoleLogger.error('IframeService.processOverlayFrames() - Error retrieving Juniper Sidecar auth codes: ', err);
            _.remove(this.overlayFrames, () => true);
            this._overlayFrames.next(this.overlayFrames);
            return of(undefined);
          }),
          switchMap((authCodes: Record<string, string>) => {
            sidecarApp.setReadyResponseReceived(false);
            ConsoleLogger.debug('IframeService.processOverlayFrames() - retrieved Juniper Sidecar auth codes: ' + authCodes);
            const authCode = authCodes ? authCodes[appInfo.appId] : null;
            ConsoleLogger.debug('IframeService.processOverlayFrames() - Juniper Sidecar auth code: ' + authCode);
            if(authCode) {
              if (sidecarApp.loadingError === true || sidecarApp.url.indexOf('unready-app-error.html') >= 0) {
                ConsoleLogger.debug('IframeService.processOverlayFrames() - Reloading Juniper Sidecar frame after error.');
                sidecarApp.loadingError = false;
                sidecarApp.url = this.fixEmbeddedUrl(appInfo.url, Application.JUNIPER_SIDECAR, authCode, appInfo?.override);
                ConsoleLogger.debug('IframeService.processOverlayFrames() - New Juniper Sidecar URL: ' + sidecarApp.url);
              } else {
                ConsoleLogger.debug('IframeService.processOverlayFrames() - Juniper Sidecar frame already loaded.');
                if (sidecarApp.url.indexOf('authCode=') < 0) { // First time loading the frame
                  ConsoleLogger.debug('IframeService.processOverlayFrames() - Loading Juniper Sidecar frame for the first time.');
                  sidecarApp.url = this.fixEmbeddedUrl(sidecarApp.url, Application.JUNIPER_SIDECAR, authCode, appInfo?.override);
                } else { // Frame is already loaded successfully
                  ConsoleLogger.debug('IframeService.processOverlayFrames() - Juniper Sidecar frame already loaded successfully.');
                  // Do not send logout/siteswitch if user is a Guest. Otherwise, if this is a logout or siteswitch, send the message since the frame must be loaded...
                  const isLogoutOrTenantChange = !this.authService.isGuestUser() && (authType === AuthType.LOGOUT || authType === AuthType.SITE_SWITCH);
                  if (isLogoutOrTenantChange) {
                    ConsoleLogger.debug('IframeService.processOverlayFrames() - Sending ' + authType + ' message to Juniper Sidecar frame.');
                    this.messagingService.sendStateMessages(sidecarApp, authCode, authType);
                  }
                }
              }
              return this.waitForApps([sidecarApp], [sidecarApp], this.getOverlayFrames())
                .pipe(takeUntil(this._cancelWaitForApps));
            } else {
              ConsoleLogger.debug('IframeService.processOverlayFrames() - Removing overlay frame: ', Application.JUNIPER_SIDECAR + ' = appId:' + appInfo.appId + ', authCode:' + authCode);
              _.remove(this.overlayFrames, () => true);
              this._overlayFrames.next(this.overlayFrames);
              return of(undefined);
            }
          }),
          take(1),
          takeUntil(this.ngUnsubscribe)
        );
    } else {
      if (sidecarApp) {
        // Send the state/tenant change message to all of the apps that are no longer visible in the iframes
        ConsoleLogger.debug('IframeService.processOverlayFrames() - Juniper Sidecar access revoked. Sending ' + authType + ' message.');
        this.messagingService.sendStateMessages(sidecarApp, undefined, authType);
        sidecarApp.accessible = false;
      } else {
        ConsoleLogger.debug('IframeService.processOverlayFrames() - Juniper Sidecar access revoked. No action needed since frame not present.');
      }
      return of(undefined);
    }
  };



  /** ----------------------------------------------------------------------------------------------------------------------- */
  /** Formerly, PortalStateService.setFramableApps() */
  public processEmbeddedFrames(apps: PortalApp[], autoActivateAppsInFrameWhenReady: boolean = true): Observable<PortalApp[]> {
    // DO NOT create a new array for embeddedFrames. Doing so will cause a complete reload of all of the iframes. Use the existing array.
    if (_.isEmpty(apps)) {
      _.remove(this.getEmbeddedFrames(), () => true);
    }

    const appIds: string = this.envService.appInfos.filter(a => a.name !== Application.JUNIPER_SIDECAR).map((a: any) => a.appId).join(',');
    return this.authService.getAppAuthCodes(appIds, 'processEmbedded')
      .pipe(
        switchMap((authCodes: any) => {
          const oldUserApps = _.cloneDeep(this.getEmbeddedFrames()).filter(f => !f.isExternal);
          const newUserApps = _.cloneDeep(apps).filter(f => !f.isExternal);
          const authType = this.authService.getAuthenticationEvent()?.authType;

          for (const availableUserApp of newUserApps) {
            const appInfo: AppInfo = this.envService.appInfos.find((ai: any) => ai.name === availableUserApp.app);
            const appId = appInfo?.appId;
            const authCode: string = authCodes[appId];

            _.remove(oldUserApps, (o) => o.app === availableUserApp.app);
            availableUserApp.url = this.envService.getUrl(availableUserApp.app);

            if(appId && authCode) {
              const alreadyFramedApp: PortalApp = this.findEmbeddedFrame(availableUserApp.app);
              if (alreadyFramedApp) {
                if (!this.DESTINY_RESET_IN_PROGRESS_HACK_INDICATOR) {
                  alreadyFramedApp.setReadyResponseReceived(false);
                }
                if (alreadyFramedApp.loadingError === true || alreadyFramedApp.url.indexOf('unready-app-error.html') >= 0) {
                  alreadyFramedApp.loadingError = false;
                  alreadyFramedApp.url = this.fixEmbeddedUrl(availableUserApp.url, availableUserApp.app, authCode, appInfo?.override);
                } else {
                  // Do not send logout/siteswitch if user is a Guest. Otherwise, if this is a logout or siteswitch, send the message since the frame must be loaded...
                  const isLogoutOrTenantChange = !this.authService.isGuestUser() && (authType === AuthType.LOGOUT || authType === AuthType.SITE_SWITCH);
                  if (isLogoutOrTenantChange || !this.isInNewlyAddedEmbeddedFrameApps(alreadyFramedApp.app)) {
                    this.sendStateMessages(availableUserApp.app, authCode, authType);
                  }
                  this.removeFromNewlyAddedEmbeddedFrameApps(alreadyFramedApp.app);
                }
              }
            } else {
              _.remove(this.getEmbeddedFrames(), (o) => o.app === availableUserApp.app);
            }
          }
          if (authType === AuthType.SITE_SWITCH) {
            const switchToName =  this.authService.getSiteGuid() ?  this.authService.getUserLogin()?.getLocationName() : this.authService.getUserLogin()?.getDistrictName();
            this.toastService.show(DialogModeType.INFO, UserMessage.siteSwitchedTo + switchToName, false);
          }

          // Send the state/tenant change message to all of the apps that are no longer visible in the iframes
          for (const oldApp of oldUserApps) {
            this.removeFromNewlyAddedEmbeddedFrameApps(oldApp.app);
            this.sendStateMessages(oldApp.app, undefined, authType);
          }

          /**
           * Give the embeddedFrames time to respond with a 'ready' message.
           *
           * Since we're now lazy loading the iframes, on the initial load of Portal, getEmbeddedFrames() will be empty.
           * On subsequent calls (whenever grants change), getEmbeddedFrames() will have the existing framed apps.
           * The embeddedFrames should be responding with 'ready' messages in response to a logout/tenantchange.
           * In that case, we will wait for those apps to respond before proceeding.
           *
           */
          this.getEmbeddedFrames().filter(f => f.isExternal).forEach(ea => ea.accessible = false);
          const appsToActivate: PortalApp[] = autoActivateAppsInFrameWhenReady ? newUserApps : [];
          return this.waitForApps(this.getEmbeddedFrames(), appsToActivate, this.getEmbeddedFrames());
      }),
      take(1),
      takeUntil(this.ngUnsubscribe)
    );
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public waitForApps(apps: PortalApp[], appsToMakeAccessible: PortalApp[], allApps: PortalApp[]): Observable<PortalApp[]> {
    return of(undefined)
      .pipe(
        delayWhen(() => this.waitForAppsToBeReady(apps, appsToMakeAccessible, allApps)),
        map(() => {
          return this.markAppsWithLoadingError(apps);
        })
      );
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  public waitForAppsToBeReady(apps: PortalApp[], appsToMakeAccessible: PortalApp[], allApps: PortalApp[]): Observable<boolean> {
    if (apps?.length === 0) {
      return of(true);
    }

    // Ensure that we are working off the actual framed apps. If an app has a loadingError, no need to wait for it.
    apps = allApps.filter(f => !f.isExternal && !f.loadingError && apps.some(a => a.app === f.app));
    const accessibleAppNames = new Set(appsToMakeAccessible.map(a => a.app));

    return new Observable<boolean>(observer => {
      const timeoutMillis = this.envService.getLogoutTimeout();
      ConsoleLogger.debug('IframeService.waitForAppsToBeReady() - Waiting up to: ' + timeoutMillis + 'ms');

      // If at the district, DD/Collections will never show. Just mark them as ready.
      if (this.authService.isDistrictUserNotAtSite() || this.authService.isDestinyAdminNotAtSite()) {
        ConsoleLogger.debug('IframeService.waitForAppsToBeReady() - Setting DD/Collections ready for user at District');
        apps.filter(app => app.app === Application.DD || app.app === Application.COLLECTIONS)
            .forEach(app => app.setReadyResponseReceived(true));
      }

      // Max time to wait for apps to be ready.
      const unreadyTimer = setTimeout(() => {
        ConsoleLogger.debug('IframeService.waitForAppsToBeReady() - Timeout reached.');

        // Any app not ready at this time should show the unready error.
        const notReadyApps = apps.filter(f => !f.isReadyResponseReceived());
        ConsoleLogger.error('IframeService.waitForAppsToBeReady() - APPS UNREADY: ' + notReadyApps.map(f => f.app));
        for (const nra of notReadyApps) {
          nra.loadingError = true;
        }

        // Make all apps accessible, even the unready ones (which will show the error page), if they are in the appsToMakeAccessible list
        apps.filter(ra => accessibleAppNames.has(ra.app)).forEach(ra => ra.accessible = true);

        observer.next(true);
        observer.complete();
      }, timeoutMillis);

      // Check for ready apps every 250ms
      let elapsedTime = 0;
      const intervalHandle = setInterval(() => {
        // Once an app is ready, mark it as accessible if it's in the appsToMakeAccessible list
        const readyApps = apps.filter(f => f.isReadyResponseReceived());
        readyApps.filter(ra => !ra.accessible && accessibleAppNames.has(ra.app)).forEach(ra => ra.accessible = true);

        // If the all apps are ready, we're done.
        if (readyApps.length === apps.length) {
          ConsoleLogger.debug('IframeService.waitForAppsToBeReady() - ALL READY: ' + readyApps.map(f => f.app));
          observer.next(true);
          observer.complete();
        } else {
          if (elapsedTime % 5000 === 0) { // Log unready apps every 5 seconds
            const notReadyApps = apps.filter(f => !f.isReadyResponseReceived());
            ConsoleLogger.debug('IframeService.waitForAppsToBeReady() - UNREADY (' + (elapsedTime/1000) +  's): ' + notReadyApps.map(f => f.app));
          }
          elapsedTime += 250;
        }
      }, 250);

      // Teardown function: cleanup timers when observable completes or is unsubscribed
      return () => {
        ConsoleLogger.debug('IframeService.waitForAppsToBeReady() - Complete');
        clearTimeout(unreadyTimer);
        clearInterval(intervalHandle);
      };
    });
  }

  /** ----------------------------------------------------------------------------------------------------------------------- */
  public getIframeElement(app: Application): HTMLIFrameElement {
    return document.getElementById(app) as HTMLIFrameElement;
  }

  /** ----------------------------------------------------------------------------------------------------------------------- */
  private fixEmbeddedUrl( url: string, app: Application, authCode?: string, override: string = 'false', siteGuidOverride?: string): string {
    const urlObj = new URL(url);

    if(!this.envService.isProductionBuild() && this.envService.shouldBeProxied && override === 'true'){
      urlObj.searchParams.set('nue-parent-url', this.envService.portal_frontend_proxy_url);
    }

    if (!urlObj.searchParams.has('portalMode')) {
      urlObj.searchParams.set('portalMode', 'true');
    }

    if (!urlObj.searchParams.has('appId')) {
      const appId = this.authService.getDistrictAppId();
      urlObj.searchParams.set('appId', appId);
    }

    if (!urlObj.searchParams.has('siteGuid')) {
      let siteGuid = siteGuidOverride || this.authService.getSiteGuid();
      if(Application.DESTINY === app) {
        siteGuid = this.authService.getSiteGuidWithoutContext(siteGuid);
      }
      urlObj.searchParams.set('siteGuid', siteGuid);
    }

    if (!urlObj.searchParams.has('portalId')) {
      const portalId = this.channelService.getPortalId();
      urlObj.searchParams.set('portalId', portalId);
    }

    if(authCode && !urlObj.searchParams.has('authCode')) {
      urlObj.searchParams.set('authCode', authCode);
    }

    const recommendedUIVer: number = this.envService.isFUN_ENABLED ? this.envService.getRecommendedUNVersion() : 1;
    urlObj.searchParams.set('recommendedUiVersion', recommendedUIVer.toString());

    if ([Application.DESTINY_EXPRESS, Application.FOLLETT_COMMUNITY, Application.TITLEWAVE, Application.JUNIPER_SIDECAR].indexOf(app) < 0) {
      ConsoleLogger.debug('IframeService.fixEmbeddedUrl() - fixurl initnavinfo: ' + JSON.stringify(this.initialNavInfoForIframeFromBookmarkUrl));
      if(this.initialNavInfoForIframeFromBookmarkUrl?.app === app && !_.isEmpty(this.initialNavInfoForIframeFromBookmarkUrl?.nav?.trim()) && !urlObj.searchParams.has('nav')) {
        urlObj.searchParams.set('nav', this.initialNavInfoForIframeFromBookmarkUrl.nav);
        this.initialNavInfoForIframeFromBookmarkUrl = undefined;
      }
    }

    return urlObj.toString();
  }



  /** ----------------------------------------------------------------------------------------------------------------------- */
  private sendStateMessages(app: Application, authCode: string, authType: AuthType) {
    const pa = this.findEmbeddedOrOverlayFrame(app);
    this.messagingService.sendStateMessages(pa, authCode, authType, this.continuationState[app]);
    delete this.continuationState[app];
  }



}
